// Tax calculation utilities for UK 2025/26 tax year

export const TAX_BANDS = {
  england: {
    personalAllowance: 12570,
    bands: [
      { min: 12570, max: 50270, rate: 0.20 },
      { min: 50270, max: 125140, rate: 0.40 },
      { min: 125140, max: Infinity, rate: 0.45 }
    ]
  },
  scotland: {
    personalAllowance: 12570,
    bands: [
      { min: 12570, max: 14876, rate: 0.19 },
      { min: 14876, max: 26561, rate: 0.20 },
      { min: 26561, max: 43662, rate: 0.21 },
      { min: 43662, max: 75000, rate: 0.42 },
      { min: 75000, max: 125140, rate: 0.45 },
      { min: 125140, max: Infinity, rate: 0.48 }
    ]
  }
};

export const NI_BANDS = [
  { min: 12570, max: 50270, rate: 0.08 },
  { min: 50270, max: Infinity, rate: 0.02 }
];

export const STUDENT_LOAN_THRESHOLDS = {
  plan1: { threshold: 22015, rate: 0.09 },
  plan2: { threshold: 27295, rate: 0.09 },
  plan4: { threshold: 27660, rate: 0.09 },
  plan5: { threshold: 25000, rate: 0.09 },
  postgraduate: { threshold: 21000, rate: 0.06 }
};

export function calculateIncomeTax(annualSalary, region) {
  const bands = TAX_BANDS[region];
  let tax = 0;

  // Personal allowance reduction for high earners
  let personalAllowance = bands.personalAllowance;
  if (annualSalary > 100000) {
    const reduction = Math.min(personalAllowance, (annualSalary - 100000) / 2);
    personalAllowance = Math.max(0, personalAllowance - reduction);
  }

  // Calculate taxable income after personal allowance
  const taxableIncome = Math.max(0, annualSalary - personalAllowance);
  
  if (taxableIncome <= 0) {
    return 0;
  }

  // Calculate tax for each band
  let remainingTaxable = taxableIncome;
  
  for (const band of bands.bands) {
    if (remainingTaxable <= 0) break;
    
    // Calculate the taxable amount in this band
    // Band boundaries are absolute, so we need to find what portion of taxable income falls in this band
    const bandLower = Math.max(0, band.min - personalAllowance);
    const bandUpper = band.max - personalAllowance;
    
    if (bandLower >= taxableIncome) break; // This band is above the taxable income
    
    const amountInBand = Math.min(
      remainingTaxable,
      Math.min(taxableIncome, bandUpper) - Math.max(0, bandLower)
    );
    
    if (amountInBand > 0) {
      tax += amountInBand * band.rate;
      remainingTaxable -= amountInBand;
    }
  }

  return Math.max(0, tax);
}

export function calculateNationalInsurance(annualSalary) {
  let ni = 0;

  for (const band of NI_BANDS) {
    if (annualSalary <= band.min) break;
    
    const niableInBand = Math.min(annualSalary - band.min, band.max - band.min);
    if (niableInBand > 0) {
      ni += niableInBand * band.rate;
    }
  }

  return Math.max(0, ni);
}

export function calculateStudentLoan(annualSalary, plan) {
  if (plan === 'none') return 0;
  
  const loanDetails = STUDENT_LOAN_THRESHOLDS[plan];
  if (!loanDetails || annualSalary <= loanDetails.threshold) return 0;
  
  return (annualSalary - loanDetails.threshold) * loanDetails.rate;
}

export function calculateTakeHomePay({ annualSalary, region, pensionPercent, studentLoanPlan }) {
  // Calculate pension contribution (pre-tax)
  const pensionContribution = (annualSalary * pensionPercent) / 100;
  const salaryAfterPension = annualSalary - pensionContribution;
  
  // Calculate taxes on salary after pension
  const incomeTax = calculateIncomeTax(salaryAfterPension, region);
  const nationalInsurance = calculateNationalInsurance(salaryAfterPension);
  const studentLoan = calculateStudentLoan(salaryAfterPension, studentLoanPlan);
  
  // Calculate net pay
  const totalDeductions = incomeTax + nationalInsurance + studentLoan + pensionContribution;
  const netAnnual = annualSalary - totalDeductions;
  
  return {
    grossSalary: annualSalary,
    incomeTax,
    nationalInsurance,
    studentLoan,
    pensionContribution,
    netPay: {
      annual: Math.max(0, netAnnual),
      monthly: Math.max(0, netAnnual / 12),
      weekly: Math.max(0, netAnnual / 52)
    }
  };
}

export function calculateHourlyRate(annualSalary, hoursPerWeek = 37.5) {
  return annualSalary / (hoursPerWeek * 52);
}

export function calculateAnnualFromHourly(hourlyRate, hoursPerWeek = 37.5) {
  return hourlyRate * hoursPerWeek * 52;
}